<script lang="ts">
  import { readRefusedRows } from "./refusedRows";
  /**
   * ORDER 23 Session 2 — CHART C, the time-less ES range chart.
   *
   * The Worker owns range-bar construction and rangeBox.ts owns structure detection. This panel
   * only renders their closed-bar evidence on one synthetic sequential timeline. Every signal-like
   * mark remains display-only and every failed leg stays visible and logged.
   */
  import { onMount } from "svelte";
  import {
    CandlestickSeries, createChart, createSeriesMarkers, HistogramSeries, LineSeries, LineStyle,
    type IChartApi, type IPriceLine, type ISeriesApi, type ISeriesMarkersPluginApi, type Time,
    type UTCTimestamp,
  } from "lightweight-charts";
  import { leg } from "./feedLeg";
  import { RANGE_CADENCE_MS } from "./rangeCadence.ts";
  import { loadZeroDte } from "./zeroDteLoad";
  import {
    fetchDealerLevels,
    subscribeServedVenueState,
    type DealerLevelsResponse,
    type VenueState,
  } from "./api";
  import { absentLevelNotesFromLevels, basisRatio, zeroDteLevels } from "./dealer";
  import StalenessChip from "./StalenessChip.svelte";
  import AgePill from "./AgePill.svelte";
  import { keptPayloadAfterFailure } from "./dataAge";
  import VenueClosedBanner from "./VenueClosedBanner.svelte";
  import { resolveTokens, watchTheme, type ResolvedTokens } from "../spike/lwcTokens";
  import {
    buildRangeChartModel,
    cullRangeStructureLabels,
    rangeLogicalSlots,
    RANGE_NORMAL_CONTRACTS,
    RANGE_NORMAL_MINUTES,
    RANGE_STRUCTURE_FLOOR,
    closedRangeSessionDate,
    rangeStructureNotice,
    type RangeChartBar,
    type RangeChartModel,
    type RangeChartLevel,
    type RangeStructureLabel,
  } from "./lwc/rangeChartModel";
  import { studySessionDate } from "./lwc/rangeBox";
  import { RangeBarPrimitive } from "./lwc/rangeBarPrimitive";
  import { RangeHaloPrimitive, type RangeHaloPrimitiveDatum } from "./lwc/rangeHaloPrimitive";
  import {
    RANGE_PAGE_LIMIT, canPageRangeHistory,
    historyFirstCursor,
    rangeOpenPlan,
    historyGapLabel,
    liveEdgeAbsent,
    liveEdgeAbsentLabel, mergeRangePages, rangePageUrl, readRangePage,
    shouldLoadOlderRange, withRangeHistory,
  } from "./rangePaging";
  import { rangeLevelsState } from "./studyPanel.ts";

  interface RangeFeed {
    ok?: boolean;
    root?: string;
    ticks?: number;
    session_cvd_before?: number | null;
    bars?: RangeChartBar[];
    age_s?: number | null;
    budget_s?: number | null;
    stale?: boolean | null;
    state?: string;
    reason?: string;
    session_verdict?: string | null;
  }

  interface Props { api: string; height?: number }
  interface FocusProps { ticks?: 8 | 12 }
  interface ClosedVenueProps { servedVenueState?: VenueState | null }
  let {
    api,
    height = 720,
    ticks = $bindable<8 | 12>(8),
    servedVenueState = null,
  }: Props & FocusProps & ClosedVenueProps = $props();

  let host: HTMLDivElement;
  let chart: IChartApi | null = null;
  let rangeBarSeries: ISeriesApi<"Candlestick"> | null = null;
  let rangeBarPrimitive: RangeBarPrimitive | null = null;
  let rangeHaloSeries: ISeriesApi<"Histogram"> | null = null;
  let rangeHaloPrimitive: RangeHaloPrimitive | null = null;
  let rangeDeltaSeries: ISeriesApi<"Histogram"> | null = null;
  let rangeCvdSeries: ISeriesApi<"Line"> | null = null;
  let rangeCostSeries: ISeriesApi<"Line"> | null = null;
  let rangeEventMarkers: ISeriesMarkersPluginApi<Time> | null = null;
  let boxSeries: ISeriesApi<"Line">[] = [];
  let levelLines: IPriceLine[] = [];
  let tokens: ResolvedTokens | null = null;
  let labelFrame = 0;

  let widthMode = $state<"minutes" | "equal">("minutes");
  let feed = $state<RangeFeed | null>(null);
  let bars = $state<RangeChartBar[]>([]);
  let model = $state<RangeChartModel | null>(null);
  let levels = $state<RangeChartLevel[]>([]);
  let levelAbsenceNotes = $state<string[]>([]);
  let levelsFeed = $state<DealerLevelsResponse | null>(null);
  let levelsFetchedAtMs = $state<number | null>(null);
  let levelsRequestKey = $state<string | null>(null);
  let relayedVenueState = $state<VenueState | null>(null);
  let relayedVenueStateKnown = $state(false);
  let effectiveVenueState = $derived(relayedVenueStateKnown
    ? (servedVenueState ?? relayedVenueState)
    : (servedVenueState ?? levelsFeed?.venue_state ?? null));
  let rangeError = $state<string | null>(null);
  let levelsError = $state<string | null>(null);
  let levelsLoadError = $state<string | null>(null);
  let levelsPresentation = $derived(rangeLevelsState(levelsError, effectiveVenueState));
  let venueClosed = $derived(levelsPresentation.venueClosed);
  let loaded = $state(false);
  let cursorIndex = $state<number | null>(null);
  let structureLabels = $state<RangeStructureLabel[]>([]);
  let infoOpen = $state(false);
  // ── ORDER 31-S2: THE BACKDATED HALF ──────────────────────────────────────────────────────────
  // `liveBars` is what the anchored poll served; `olderBars` is what the cursor door has handed
  // back so far. They are kept APART and merged into `bars` at draw time, so a 60 s live refresh
  // cannot silently drop the history the owner panned into.
  let liveBars = $state<RangeChartBar[]>([]);
  let olderBars = $state<RangeChartBar[]>([]);
  /** The session CVD before the OLDEST drawn bar — it moves with the series. See rangePaging.ts. */
  let drawnBaseline = $state<number | null>(null);
  let rangeNextBefore = $state<number | null>(null);
  let rangeHasMore = $state(false);
  let historyDepth = $state(0);
  let historyLoading = $state(false);
  let historyError = $state<string | null>(null);
  let historyTableAbsent = $state(false);
  let historyEpoch = 0;
  let historyGestureArmed = false;
  let historyGestureAt = 0;
  const HISTORY_GESTURE_WINDOW_MS = 1_500;
  let rangeGeneration = 0;
  let levelsGeneration = 0;
  let fitOnNextPaint = true;
  const RANGE_PILLS = [{ ticks: 8 as const, label: "8t" }, { ticks: 12 as const, label: "12t" }];
  const RANGE_REFRESH_MS = 60_000;

  let lastBarMs = $derived(bars.at(-1)?.t1 ?? null);
  let lastBarSpanMs = $derived(Math.max(1, (bars.at(-1)?.t1 ?? 0) - (bars.at(-1)?.t0 ?? 0)));
  let cursorPoint = $derived(cursorIndex === null ? null : model?.points[cursorIndex] ?? null);
  let latestBreak = $derived(model?.breaks.at(-1) ?? null);
  let latestBox = $derived(model?.activeBox ?? latestBreak?.box ?? null);
  let latestBoxIsLive = $derived(model?.activeBox === latestBox && latestBox !== null);
  let closedRangeSession = $derived(closedRangeSessionDate(bars, {
    sessionVerdict: feed?.session_verdict ?? null, nowMs: Date.now(),
  }));
  let structureNotice = $derived(rangeStructureNotice(model?.points ?? []));
  let newestSessionDate = $derived(model?.points.at(-1)
    ? studySessionDate(model!.points.at(-1)!.t1) : null);

  // ORDER 82 (2) — the pill's text, or null when there is nothing to qualify. `bars` is what is
  // DRAWN, so the pill appears only once lake bars are actually on the ink and clears the instant
  // a live bar arrives, without a timer or a second flag to fall out of step with the chart.
  // ORDER 82 r2 — the live leg does not reach the lake (a six-day hole at 10:55Z 09-04). Named so
  // the owner is not shown one continuous series across a gap the data does not have.
  let historyGapPill = $derived(
    liveBars.length > 0 && olderBars.length > 0
      ? historyGapLabel(olderBars.at(-1)?.t1 ?? null, liveBars[0]?.t0 ?? null)
      : null,
  );

  let liveEdgeAbsentPill = $derived(
    liveEdgeAbsent(liveBars.length, bars.length)
      ? liveEdgeAbsentLabel(bars.at(-1)?.t1 ?? null)
      : null,
  );

  const asTime = (seconds: number) => seconds as UTCTimestamp;
  const formatContracts = (value: number) => {
    const sign = value > 0 ? "+" : value < 0 ? "−" : "";
    const abs = Math.abs(value);
    return `${sign}${abs >= 1_000 ? `${(abs / 1_000).toFixed(1)}k` : Math.round(abs)}`;
  };
  const hhmm = (ms: number) => new Date(ms).toISOString().slice(11, 16) + "Z";
  const alpha = (color: string, opacity: number) => {
    const hex = /^#([0-9a-f]{6})$/i.exec(color);
    if (!hex) return color;
    const channel = Math.round(Math.max(0, Math.min(1, opacity)) * 255).toString(16).padStart(2, "0");
    return `${color}${channel}`;
  };

  function rangeRefusal(raw: RangeFeed | null, selected: 8 | 12): string | null {
    if (!raw?.ok) return raw?.reason ?? "range route refused the request";
    if (raw.root !== "ES") return `range route served root ${raw.root ?? "UNREPORTED"}; expected ES`;
    if (raw.ticks !== selected) return `range route served ${raw.ticks ?? "UNREPORTED"}t; requested ${selected}t`;
    if (!Array.isArray(raw.bars) || raw.bars.length === 0) {
      return raw.session_verdict === "CLOSED" ? null : raw.reason ?? "no complete ES range bars";
    }
    if (!Number.isFinite(raw.session_cvd_before)) return "range route omitted the session CVD baseline";
    return null;
  }

  function rebuildModel() {
    try {
      // ⛔ THE BASELINE IS THE DRAWN ONE, NOT THE LIVE FEED'S. `cumulativeBaseline` is the CVD
      // level BEFORE THE FIRST DRAWN BAR; once history is prepended the first drawn bar is an
      // older one, and reading `feed.session_cvd_before` here would displace the whole CVD line by
      // the sum of the prepended contracts — a line with a plausible shape and a wrong level.
      model = bars.length ? buildRangeChartModel(bars, {
        ticks, widthMode, levels, cumulativeBaseline: drawnBaseline!,
        sessionVerdict: feed?.session_verdict ?? null, nowMs: Date.now(),
      }) : null;
      if (bars.length) rangeError = null;
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      console.error(`[RangeChartPanel] RANGE MODEL ERROR — ${reason}`);
      rangeError = reason;
      model = null;
    }
    feedSeries();
  }

  function remapLevels() {
    levels = [];
    levelAbsenceNotes = [];
    levelsError = levelsLoadError;
    if (!levelsFeed) return;
    if (levelsFeed.root !== "SPX" || levelsFeed.ticker !== "SPX") {
      levelsError = `levels route served ${levelsFeed.root}/${levelsFeed.ticker}; expected SPX/SPX`;
      return;
    }
    const esLast = bars.at(-1)?.c ?? null;
    const ratio = basisRatio(levelsFeed.spot ?? null, esLast);
    if (levelsFeed.ok && ratio === null) {
      levelsError = "ES/SPX basis unavailable; refusing unmapped SPX levels";
      return;
    }
    if (levelsFeed.ok && ratio !== null) {
      levelAbsenceNotes = absentLevelNotesFromLevels(levelsFeed);
      levels = zeroDteLevels(levelsFeed, ratio).map((level) => ({
        price: level.es,
        label: level.spx.toFixed(0),
        kind: level.cls,
      }));
    }
  }

  /** Commit `liveBars` + `olderBars` into the drawn series and its baseline, in one place. */
  function commitDrawn() {
    const drawn = withRangeHistory(liveBars, feed?.session_cvd_before ?? null, olderBars,
      olderBars.length ? drawnBaseline : null);
    bars = drawn.bars;
    drawnBaseline = drawn.baseline;
  }

  /**
   * ORDER 31-S2 — a rung change INVALIDATES every history bar AND the cursor that named them.
   * An 8t cursor is a `t0` from the 8t geometry; spending it against the 12t rung would ask for
   * "the 12t bars before an 8t bar's open", which the route answers honestly and which draws as a
   * seam nothing can explain.
   */
  function resetHistory() {
    olderBars = [];
    rangeNextBefore = null;
    rangeHasMore = false;
    historyDepth = 0;
    historyError = null;
    historyTableAbsent = false;
    historyEpoch += 1;
    historyLoading = false;
    historyGestureArmed = false;
  }

  async function loadRange(selected: 8 | 12 = ticks) {
    const mine = ++rangeGeneration;
    if (selected !== ticks) { fitOnNextPaint = true; resetHistory(); }
    ticks = selected;
    loaded = false;
    const response = await leg(`${api}/api/es/cvd/range?ticks=${selected}&limit=300`, "range-chart", RANGE_CADENCE_MS);
    if (mine !== rangeGeneration) return;
    if (response.transport !== null) {
      rangeError = response.transport;
      feed = { ok: false, ticks: selected, reason: response.transport };
      liveBars = [];
      bars = [];
      model = null;
      loaded = true;
      console.error(`[RangeChartPanel] RANGE FEED ERROR — ${response.transport}`);
      feedSeries();
      return;
    }
    const next = response.body as RangeFeed | null;
    const refused = rangeRefusal(next, selected);
    feed = next;
    if (refused !== null) {
      rangeError = refused;
      liveBars = [];
      // ⛔ ORDER 82 (3) — THE DRAWN LAKE SURVIVES A REFUSED LIVE LEG. `bars = []` here wiped the
      // history on the 60 s timer: the owner panned into the lake, the next poll found the live
      // edge still empty, and the chart blanked itself with no gesture and no explanation.
      // `commitDrawn` with an empty live leg redraws exactly the lake that is already held.
      commitDrawn();
      console.error(`[RangeChartPanel] RANGE FEED ${next?.bars?.length ? "ERROR" : "EMPTY"} — ${refused}`);
      // ⭐ ORDER 82 (1)+(3) — HISTORY FIRST, AND THE INK SURVIVES THE REFRESH. One decision, taken
      // in `rangeOpenPlan` so it can be driven by fixtures; what is left here is assignment.
      // ⛔ `model = null` USED TO SIT ABOVE THIS, WITH `rebuildModel()` REACHABLE ONLY WHEN A SEED
      // WAS ISSUED. The seed is null once history is held, so the 60 s refresh nulled the model and
      // `feedSeries()` cleared every series — the panel opened on the lake and blanked a minute
      // later, still wearing the pill. The series are fed from `model`, never from `bars`.
      const plan = rangeOpenPlan(0, null, bars.length, historyDepth, selected, Date.now());
      if (plan.cursor !== null) {
        rangeNextBefore = plan.cursor;
        rangeHasMore = plan.hasMore;
      }
      if (plan.fetchOnce) {
        await loadOlderRange();
        if (mine !== rangeGeneration) return;
      }
      if (plan.rebuild) rebuildModel();
      else model = null;
    } else {
      liveBars = next!.bars!;
      // ⛔ THE HISTORY THE OWNER PANNED INTO SURVIVES THE 60 s REFRESH. Dropping `olderBars` here
      // would silently snap the chart back to 300 bars on a timer the owner never asked for.
      commitDrawn();
      rangeError = null;
      // ORDER 31-S2 — SEED THE CURSOR. The anchored response carries no `next_before` (it is not a
      // page), so the first cursor is the OLDEST LIVE BAR'S OPEN, exclusive: the history door
      // serves what precedes it, and the seam cannot repeat the bar that named it.
      // ⭐ ORDER 82 r2 — HISTORY FIRST ON EVERY OPEN, not only when the live leg is empty. r1
      // seeded here and never FETCHED, so a sparse live leg (seven bars at 10:55Z 09-04) opened on
      // a near-empty chart with a six-day hole behind it — the owner's "still broken". The same
      // reducer answers both branches; only the cursor's origin differs.
      const plan = rangeOpenPlan(
        liveBars.length, liveBars[0]?.t0 ?? null, olderBars.length, historyDepth, selected, Date.now(),
      );
      if (plan.cursor !== null) {
        rangeNextBefore = plan.cursor;
        rangeHasMore = plan.hasMore;
      }
      console.info(`[RangeChartPanel] RANGE ${selected}t recovered — ${liveBars.length} closed ES bar(s); ${olderBars.length} lake bar(s) held.`);
      remapLevels();
      if (plan.fetchOnce) {
        await loadOlderRange();
        if (mine !== rangeGeneration) return;
      }
      rebuildModel();
    }
    loaded = true;
    feedSeries();
  }

  /**
   * ORDER 31-S2 — ONE PAGE BACKWARDS THROUGH THE LAKE.
   *
   * ⛔ THE VIEWPORT IS RESTORED BY INDEX SHIFT, NOT BY REPLAY. CHART C's x-axis is a BAR INDEX, so
   * prepending N bars moves every already-drawn bar N slots to the right. Restoring the captured
   * logical range verbatim would leave the owner staring at N bars earlier than where they let go —
   * the chart would appear to keep sliding on its own after every fetch. `+ prepended` is what
   * "the viewport did not move" MEANS on a logical axis.
   *
   * ⛔ AND AN EMPTY PAGE STOPS THE PAGER RATHER THAN RAISING ONE. `has_more:false` off the oldest
   * end of the lake — and the UNAPPLIED-DDL state, where the table does not exist at all — are both
   * honest successes. The absent-table case is recorded for the census and named in the console;
   * it is not an error chip, because nothing is broken until the desk applies the DDL.
   */
  async function loadOlderRange() {
    if (historyLoading || !rangeHasMore || rangeNextBefore === null) return;
    // ⛔⛔ ORDER 82 — THE `liveBars.length === 0` CLAUSE THAT USED TO SIT HERE WAS A SECOND GATE,
    // and leaving it would have made ORDER 82's cure INERT exactly as INT-282's PIT pill was: the
    // cursor seeded, the pager called, and the call returning on its first line. It guarded the
    // days when the ONLY cursor came from a live bar; the cursor check on the line above is the
    // real precondition now, and it is stricter — it demands the cursor itself, not a proxy for it.
    if (!canPageRangeHistory(ticks)) return;
    const epoch = historyEpoch;
    const rung = ticks;
    let url: string;
    try {
      // `rangePageUrl` THROWS on a rung or cursor it will not sign; built outside the try that
      // owns the fetch so a programming fault cannot be reported as a transport failure.
      url = `${api}${rangePageUrl(rung, RANGE_PAGE_LIMIT, rangeNextBefore)}`;
    } catch (error) {
      historyError = error instanceof Error ? error.message : String(error);
      console.error(`[RangeChartPanel] RANGE HISTORY CURSOR REFUSED — ${historyError}`);
      rangeHasMore = false;
      return;
    }
    historyLoading = true;
    try {
      const response = await leg(url, "range-history");
      if (epoch !== historyEpoch || rung !== ticks) return;
      if (response.transport !== null) {
        historyError = response.transport;
        console.error(`[RangeChartPanel] RANGE HISTORY ERROR — ${response.transport}`);
        return;
      }
      const read = readRangePage(response.body, rung, "ES");
      if (read.page === null) {
        historyError = read.blocked;
        console.error(`[RangeChartPanel] RANGE HISTORY REFUSED — ${read.blocked}`);
        return;
      }
      historyError = null;
      historyTableAbsent = !read.page.tablePresent;
      if (read.page.pageEmpty) {
        rangeHasMore = false;
        rangeNextBefore = null;
        console.info(`[RangeChartPanel] RANGE HISTORY ${rung}t EMPTY — ` +
          `${read.page.tablePresent ? "oldest end of the lake" : "es_range_bars_v1 absent (ORDER 31 DDL unapplied)"}.`);
        return;
      }
      const captured = chart?.timeScale().getVisibleLogicalRange() ?? null;
      const before = bars.length;
      olderBars = mergeRangePages(read.page.bars, olderBars);
      // The oldest page's own measured baseline becomes the drawn one — see rangePaging.ts.
      drawnBaseline = read.page.sessionCvdBefore;
      rangeNextBefore = read.page.nextBefore;
      rangeHasMore = read.page.hasMore;
      historyDepth += 1;
      commitDrawn();
      remapLevels();
      // `fitOnNextPaint` stays FALSE: a fit-content after a prepend would throw the owner's
      // viewport to the whole lake, which is the opposite of what a pan asked for.
      rebuildModel();
      const prepended = bars.length - before;
      if (captured !== null && prepended > 0) {
        chart?.timeScale().setVisibleLogicalRange({
          from: captured.from + prepended, to: captured.to + prepended,
        });
      }
      console.info(`[RangeChartPanel] RANGE HISTORY ${rung}t page ${historyDepth} — ` +
        `+${prepended} lake bar(s), ${bars.length} drawn.`);
    } catch (error) {
      historyError = error instanceof Error ? error.message : String(error);
      console.error(`[RangeChartPanel] RANGE HISTORY ERROR — ${historyError}`);
    } finally {
      if (epoch === historyEpoch) historyLoading = false;
      publishReadback();
    }
  }

  /**
   * The pan/zoom-out trigger. GESTURE-GATED, exactly as ORDER 44's is: `setVisibleLogicalRange`
   * fires this same callback, so an ungated trigger would recurse — the restore above would look
   * like a fresh pan and page again immediately, forever.
   */
  function handleVisibleLogicalRangeChange() {
    queueStructureLabels();
    publishLogicalRange();
    const recentGesture = historyGestureArmed &&
      Date.now() - historyGestureAt < HISTORY_GESTURE_WINDOW_MS;
    if (!recentGesture) return;
    const logical = chart?.timeScale().getVisibleLogicalRange() ?? null;
    if (!shouldLoadOlderRange(logical, bars.length, ticks)) return;
    historyGestureArmed = false;
    void loadOlderRange();
  }

  function armHistoryGesture() {
    historyGestureArmed = true;
    historyGestureAt = Date.now();
  }

  async function loadLevels() {
    const mine = ++levelsGeneration;
    const requestKey = "/api/dealer/levels";
    const state = await loadZeroDte(() => fetchDealerLevels(api));
    if (mine !== levelsGeneration) return;
    if (state.levels !== null) {
      levelsFeed = state.levels;
      levelsFetchedAtMs = Date.now();
      levelsRequestKey = requestKey;
    } else {
      levelsFeed = keptPayloadAfterFailure(levelsFeed, levelsRequestKey, requestKey);
      if (levelsFeed === null) {
        levelsFetchedAtMs = null;
        levelsRequestKey = null;
      }
    }
    levelsLoadError = state.fault?.label ?? state.error;
    remapLevels();
    if (state.fault && levelsPresentation.logError) console.error(state.fault.label);
    rebuildModel();
  }

  function clearStructureSeries() {
    if (!chart) return;
    for (const series of boxSeries) chart.removeSeries(series);
    boxSeries = [];
  }

  function updateStructureLabels() {
    if (!model || !rangeBarSeries) {
      structureLabels = [];
      return;
    }
    const candidates = model.readback.rangeBox.flatMap<RangeStructureLabel>((box, index) => {
      const first = model!.points[box.startIndex];
      const last = model!.points[box.endIndex];
      if (!first || !last) return [];
      const left = chart?.timeScale().timeToCoordinate(asTime(first.syntheticStartTime));
      const right = chart?.timeScale().timeToCoordinate(asTime(last.syntheticTime));
      const top = rangeBarSeries!.priceToCoordinate(box.upper);
      if ([left, right, top].some((value) => value === null || !Number.isFinite(value))) return [];
      const breakForBox = model!.breaks.find((entry) =>
        entry.box.startIndex === box.startIndex && entry.box.endIndex === box.endIndex);
      const text = breakForBox ? (breakForBox.direction === "up" ? "BREAK ▲" : "BREAK ▼") :
        box.live ? "LIVE BOX" : "RANGE BOX";
      return [{
        id: `range-box-${index}`, left: Math.min(Number(left), Number(right)) + 4,
        top: Number(top) - 20, width: Math.max(58, text.length * 7 + 8), height: 16, text,
      }];
    });
    // timeToCoordinate is pane-relative, so the time scale's width is the pane a label must fit inside.
    structureLabels = cullRangeStructureLabels(candidates, chart?.timeScale().width() ?? 0);
  }

  function queueStructureLabels() {
    cancelAnimationFrame(labelFrame);
    labelFrame = requestAnimationFrame(updateStructureLabels);
  }

  function paint(next: ResolvedTokens) {
    tokens = next;
    chart?.applyOptions({
      layout: { background: { color: next["--surface"] }, textColor: next["--muted"], attributionLogo: false },
      grid: { vertLines: { color: next["--line"] }, horzLines: { color: next["--line"] } },
      rightPriceScale: { borderColor: next["--line"] },
      timeScale: { borderColor: next["--line"] },
    });
    rangeBarSeries?.applyOptions({
      upColor: next["--up"], downColor: next["--dn"], borderVisible: false,
      wickUpColor: next["--up"], wickDownColor: next["--dn"],
    });
    rangeCvdSeries?.applyOptions({ color: next["--cvd"], lineWidth: 1 });
    rangeCostSeries?.applyOptions({ color: next["--faint"], lineWidth: 1, lineStyle: LineStyle.Dashed });
    feedSeries();
  }

  function feedSeries() {
    if (!model || !tokens || !rangeBarSeries || !rangeHaloSeries || !rangeDeltaSeries ||
        !rangeCvdSeries || !rangeCostSeries || !rangeEventMarkers) {
      cancelAnimationFrame(labelFrame);
      structureLabels = [];
      rangeBarSeries?.setData([]);
      rangeHaloSeries?.setData([]);
      rangeDeltaSeries?.setData([]);
      rangeCvdSeries?.setData([]);
      rangeCostSeries?.setData([]);
      rangeBarPrimitive?.update([]);
      rangeBarPrimitive?.setSessionBoundary(null);
      rangeHaloPrimitive?.update([]);
      clearStructureSeries();
      if (rangeBarSeries) {
        for (const line of levelLines) rangeBarSeries.removePriceLine(line);
      }
      levelLines = [];
      publishReadback();
      return;
    }
    const t = tokens;
    const pointIsMuted = (point: RangeChartModel["points"][number] | undefined) =>
      point !== undefined && newestSessionDate !== null && studySessionDate(point.t1) !== newestSessionDate;
    rangeBarSeries.setData(rangeLogicalSlots(model.points).map(({ time, point }) => point ? ({
      time: asTime(time), open: point.o, high: point.h, low: point.l, close: point.c,
      color: alpha(pointIsMuted(point) ? t["--muted"] : point.contracts >= 0 ? t["--cvd-pos"] : t["--cvd-neg"], 0),
      wickColor: alpha(pointIsMuted(point) ? t["--muted"] : point.contracts >= 0 ? t["--cvd-pos"] : t["--cvd-neg"], 0),
      borderColor: alpha(pointIsMuted(point) ? t["--muted"] : point.contracts >= 0 ? t["--cvd-pos"] : t["--cvd-neg"], 0),
    }) : ({ time: asTime(time) })));
    rangeBarPrimitive?.update(model.points.map((point) => ({
      startTime: asTime(point.syntheticStartTime), endTime: asTime(point.syntheticTime),
      open: point.o, high: point.h, low: point.l, close: point.c,
      color: pointIsMuted(point) ? t["--muted"] : point.contracts >= 0 ? t["--cvd-pos"] : t["--cvd-neg"],
    })));
    rangeHaloSeries.setData(model.readback.rangeHalo.map((point) => ({
      time: asTime(point.time), value: point.value,
      color: alpha(pointIsMuted(model!.points.find((candidate) => candidate.syntheticTime === point.time))
        ? t["--muted"] : point.value >= 0 ? t["--cvd-pos-hot"] : t["--cvd-neg-hot"], 0),
    })));
    rangeHaloPrimitive?.update(model.readback.rangeHalo.flatMap<RangeHaloPrimitiveDatum>((point) => {
      const sourcePoint = model!.points.find((candidate) => candidate.syntheticTime === point.time);
      const color = pointIsMuted(sourcePoint) ? t["--muted"]
        : point.value >= 0 ? t["--cvd-pos-hot"] : t["--cvd-neg-hot"];
      return point.glow === "hot" ? [
        { time: asTime(point.time), value: point.value, color: alpha(color, .35), widthMultiplier: 2.4 as const },
        { time: asTime(point.time), value: point.value, color: alpha(color, .25), widthMultiplier: 1.6 as const },
      ] : [{ time: asTime(point.time), value: point.value, color: alpha(color, .18), widthMultiplier: 1.8 as const }];
    }));
    rangeDeltaSeries.setData(model.points.map((point) => ({
      time: asTime(point.syntheticTime), value: point.contracts,
      color: pointIsMuted(point) ? t["--muted"] : point.glow === "hot"
        ? (point.contracts >= 0 ? t["--cvd-pos-hot"] : t["--cvd-neg-hot"])
        : (point.contracts >= 0 ? t["--cvd-pos"] : t["--cvd-neg"]),
    })));
    rangeCvdSeries.setData(model.readback.rangeCvd.map((point) => ({ time: asTime(point.time), value: point.value })));
    rangeCostSeries.setData(model.readback.rangeCost.map((point) => ({ time: asTime(point.time), value: point.value })));
    const eventMarkers = [
      ...model.readback.rangeFlip.map((marker) => ({
        time: asTime(marker.time), position: "aboveBar" as const, shape: "circle" as const,
        color: t["--flip"], size: 1,
      })),
      ...model.readback.rangeAbsorbed.map((marker) => ({
        time: asTime(marker.time), position: "inBar" as const, shape: "square" as const,
        color: t["--ink"], size: 1,
      })),
      ...model.readback.rangeBreak.map((entry) => ({
        time: asTime(entry.time), position: entry.direction === "up" ? "belowBar" as const : "aboveBar" as const,
        shape: entry.direction === "up" ? "arrowUp" as const : "arrowDown" as const,
        color: entry.gatesPassed >= 3 ? t["--stale"] : t["--muted"],
        size: 2,
      })),
    ];
    const initiativeMarkers = model.points.flatMap((point) => point.initiative ? [{
      time: asTime(point.syntheticTime), position: "inBar" as const, shape: "square" as const,
      color: pointIsMuted(point) ? t["--muted"] : t["--ink"], size: 1,
    }] : []);
    rangeEventMarkers.setMarkers([...eventMarkers, ...initiativeMarkers]);

    const boundaryPoint = model.points.find((point) => !pointIsMuted(point));
    rangeBarPrimitive?.setSessionBoundary(
      boundaryPoint && pointIsMuted(model.points[0]) ? asTime(boundaryPoint.syntheticStartTime) : null,
      t["--muted"],
    );
    clearStructureSeries();
    for (const box of model.readback.rangeBox) {
      const start = model.points[box.startIndex]?.syntheticTime;
      const end = model.points[box.endIndex]?.syntheticTime;
      if (start === undefined || end === undefined) continue;
      for (const price of [box.lower, box.upper]) {
        const series = chart!.addSeries(LineSeries, {
          title: "", color: t["--stale"], lineWidth: 1, lineStyle: LineStyle.Dashed,
          priceLineVisible: false, lastValueVisible: false,
        }, 0);
        series.setData([{ time: asTime(start), value: price }, { time: asTime(end), value: price }]);
        boxSeries.push(series);
      }
    }
    for (const line of levelLines) rangeBarSeries.removePriceLine(line);
    levelLines = model.readback.rangeLevels.map((level) => rangeBarSeries!.createPriceLine({
      price: level.price,
      title: level.label,
      color: level.kind === "gammaflip" ? t["--flip"] : t["--magnet"],
      lineWidth: 1,
      lineStyle: level.kind === "gammaflip" ? LineStyle.Dashed : LineStyle.Solid,
      axisLabelVisible: true,
    }));
    if (fitOnNextPaint) {
      chart?.timeScale().fitContent();
      fitOnNextPaint = false;
    }
    publishReadback();
    queueStructureLabels();
  }

  function publishReadback() {
    if (!host) return;
    // Read from LWC itself: `series.data()` is the observable of what the chart was actually fed.
    const count = (series: { data(): readonly unknown[] } | null) => series?.data().length ?? 0;
    host.dataset.seriesRangebar = String(count(rangeBarSeries));
    host.dataset.seriesRangedelta = String(count(rangeDeltaSeries));
    host.dataset.seriesRangehalo = String(count(rangeHaloSeries));
    host.dataset.seriesRangecvd = String(count(rangeCvdSeries));
    host.dataset.seriesRangecost = String(count(rangeCostSeries));
    host.dataset.seriesRangelevels = String(model?.readback.rangeLevels.length ?? 0);
    host.dataset.seriesRangebox = String(model?.readback.rangeBox.length ?? 0);
    host.dataset.seriesRangebreak = String(model?.readback.rangeBreak.length ?? 0);
    host.dataset.seriesRangeflip = String(model?.readback.rangeFlip.length ?? 0);
    host.dataset.seriesRangeabsorbed = String(model?.readback.rangeAbsorbed.length ?? 0);
    host.dataset.rangeStructure = structureNotice ?? "";
    host.dataset.rangeClosedSession = closedRangeSession ?? "";
    host.dataset.rangeTicks = String(ticks);
    host.dataset.rangeWidth = widthMode;
    host.dataset.paneCount = String(chart?.panes().length ?? 0);
    host.dataset.paneHeights = JSON.stringify(chart?.panes().map((pane) => pane.getHeight()) ?? []);
    // ── ORDER 31-S2 · PROVENANCE IS DATA, NEVER INK (R1106) ─────────────────────────────────────
    // A lake bar and a live bar conform BY CONSTRUCTION — S1's derivation is a pinned port of the
    // Worker's own builder and the history door re-derives side/effort/absorbed with transcribed
    // arithmetic, measured field-by-field in `rangeHistory.selftest.ts`. There is therefore
    // nothing TRUE to say about a lake bar that a viewer needs to see, and marking one would be a
    // visible claim of difference where none was measured. These attributes exist so the census
    // can PROVE the prepend happened; the chart draws exactly one series.
    //
    // ⚠ THE LAKE COUNT IS DERIVED FROM THE DRAWN SET, NOT FROM `olderBars.length`. The two differ
    // whenever the seam de-duplicates, and a census that read the accumulator would grade the
    // fetch rather than the chart. `bars` is what `rangeBarSeries` was fed.
    const liveOldest = liveBars[0]?.t0 ?? null;
    const drawnLake = liveOldest === null ? 0 : bars.filter((bar) => bar.t0 < liveOldest).length;
    host.dataset.rangeLakeBars = String(drawnLake);
    host.dataset.rangeLiveBars = String(bars.length - drawnLake);
    host.dataset.rangeHistoryDepth = String(historyDepth);
    host.dataset.rangeHistoryLoading = historyLoading ? "1" : "0";
    host.dataset.rangeHasMore = rangeHasMore ? "1" : "0";
    host.dataset.rangeNextBefore = rangeNextBefore === null ? "" : String(rangeNextBefore);
    host.dataset.rangeHistoryTable = historyTableAbsent ? "absent" : "present";
    host.dataset.rangeBaseline = drawnBaseline === null ? "" : String(drawnBaseline);
    // `count:oldestT0:newestT0` — the SessionCvdPane `fed` idiom, so one census helper reads both.
    host.dataset.rangeFed = `${bars.length}:${bars[0]?.t0 ?? ""}:${bars.at(-1)?.t0 ?? ""}`;
    publishLogicalRange();
  }

  // The viewport receipt, `from:to` in LOGICAL (bar-index) units. Published after the post-prepend
  // restore AND on every viewport move, so a gate can capture the owner's window before a page
  // and see a `fitOnNextPaint` that fired on a pan as a window spanning the whole drawn set.
  function publishLogicalRange() {
    if (!host) return;
    const logical = chart?.timeScale().getVisibleLogicalRange() ?? null;
    host.dataset.rangeLogical = logical === null
      ? ""
      : `${logical.from.toFixed(2)}:${logical.to.toFixed(2)}`;
  }

  function selectNearest(time: Time | undefined) {
    if (!model || time === undefined) { cursorIndex = null; return; }
    const target = Number(time);
    let best = 0;
    for (let index = 1; index < model.points.length; index += 1) {
      if (Math.abs(model.points[index].syntheticTime - target) < Math.abs(model.points[best].syntheticTime - target)) best = index;
    }
    cursorIndex = best;
  }

  function onkeydown(event: KeyboardEvent) {
    if (!model?.points.length || !["ArrowLeft", "ArrowRight"].includes(event.key)) return;
    event.preventDefault();
    const delta = event.key === "ArrowRight" ? 1 : -1;
    cursorIndex = Math.max(0, Math.min(model.points.length - 1, (cursorIndex ?? model.points.length - 1) + delta));
    const point = model.points[cursorIndex];
    chart?.setCrosshairPosition(point.c, asTime(point.syntheticTime), rangeBarSeries!);
  }

  onMount(() => {
    let levelsStarted = false;
    const startLevels = () => {
      if (levelsStarted) return;
      levelsStarted = true;
      void loadLevels();
    };
    const unsubscribeServedVenueState = subscribeServedVenueState((state, known) => {
      relayedVenueState = state;
      relayedVenueStateKnown = known;
      if (known) startLevels();
    });
    tokens = resolveTokens(host);
    chart = createChart(host, {
      autoSize: true,
      layout: { attributionLogo: false },
      crosshair: { mode: 0 },
      timeScale: {
        timeVisible: false,
        secondsVisible: false,
        tickMarkFormatter: (time: Time) => {
          return "";
        },
      },
      localization: { timeFormatter: (time: Time) => `bar ${Math.max(1, (model?.points.findIndex((point) => point.syntheticTime === Number(time)) ?? 0) + 1)}` },
    });
    rangeBarSeries = chart.addSeries(CandlestickSeries, {
      title: "", lastValueVisible: true,
    }, 0);
    rangeBarPrimitive = new RangeBarPrimitive(rangeBarSeries, (widths) => {
      host.dataset.rangePixelWidths = JSON.stringify(widths.map((width) => Number(width.toFixed(2))));
    });
    rangeBarSeries.attachPrimitive(rangeBarPrimitive);
    rangeEventMarkers = createSeriesMarkers(rangeBarSeries, []);
    rangeHaloSeries = chart.addSeries(HistogramSeries, {
      title: "", priceScaleId: "range-delta", priceLineVisible: false, lastValueVisible: false,
    }, 1);
    rangeHaloPrimitive = new RangeHaloPrimitive(rangeHaloSeries, (widths) => {
      host.dataset.rangeHaloPixelWidths = JSON.stringify(widths.map((width) => Number(width.toFixed(2))));
    });
    rangeHaloSeries.attachPrimitive(rangeHaloPrimitive);
    rangeDeltaSeries = chart.addSeries(HistogramSeries, {
      title: "", priceScaleId: "range-delta", priceLineVisible: false, lastValueVisible: false,
    }, 1);
    rangeCostSeries = chart.addSeries(LineSeries, {
      title: "", priceScaleId: "range-cost", priceLineVisible: false, lastValueVisible: false,
      priceFormat: { type: "custom", formatter: (value: number) => `${value.toFixed(1)}×` },
    }, 1);
    rangeCvdSeries = chart.addSeries(LineSeries, {
      title: "", priceScaleId: "left", priceLineVisible: false, lastValueVisible: false,
    }, 2);
    rangeDeltaSeries.priceScale().applyOptions({ visible: false, scaleMargins: { top: .12, bottom: .12 } });
    rangeCostSeries.priceScale().applyOptions({ visible: true, scaleMargins: { top: .12, bottom: .12 } });
    rangeCostSeries.createPriceLine({
      price: 1, title: "1×", color: tokens["--faint"], lineWidth: 1,
      lineStyle: LineStyle.Dashed, axisLabelVisible: true,
    });
    rangeCvdSeries.priceScale().applyOptions({ visible: true, scaleMargins: { top: .08, bottom: .08 } });
    const panes = chart.panes();
    panes[0]?.setStretchFactor(420);
    panes[1]?.setStretchFactor(190);
    panes[2]?.setStretchFactor(100);
    chart.subscribeCrosshairMove((param) => selectNearest(param.time));
    chart.timeScale().subscribeVisibleLogicalRangeChange(handleVisibleLogicalRangeChange);
    // ORDER 31-S2: a pan or a zoom-out is a GESTURE, and only a gesture arms the pager. Capture
    // phase, so LWC's own handlers cannot swallow the arming before it is recorded.
    host.addEventListener("pointerdown", armHistoryGesture, { capture: true });
    host.addEventListener("wheel", armHistoryGesture, { capture: true, passive: true });
    const edgeObserver = new ResizeObserver(queueStructureLabels);
    edgeObserver.observe(host);
    const paneObserver = new MutationObserver(queueStructureLabels);
    paneObserver.observe(host, {
      subtree: true, attributes: true, attributeFilter: ["style", "height", "width"],
    });
    const stopTheme = watchTheme(host, paint);
    paint(tokens);
    void loadRange();
    const rangeTimer = setInterval(() => void loadRange(), RANGE_REFRESH_MS);
    const levelsTimer = setInterval(() => {
      if (relayedVenueStateKnown) void loadLevels();
    }, 180_000);
    return () => {
      stopTheme();
      clearInterval(rangeTimer);
      clearInterval(levelsTimer);
      unsubscribeServedVenueState();
      chart?.timeScale().unsubscribeVisibleLogicalRangeChange(handleVisibleLogicalRangeChange);
      host.removeEventListener("pointerdown", armHistoryGesture, { capture: true });
      host.removeEventListener("wheel", armHistoryGesture, { capture: true });
      edgeObserver.disconnect();
      paneObserver.disconnect();
      cancelAnimationFrame(labelFrame);
      chart?.remove();
      chart = null;
    };
  });
  // R1/R3: retain a named malformed alert instead of dropping the map.
  let refusedRows = $derived.by(() => {
    if (levelsFeed === null) return { state: "NO_BODY" as const };
    try { return readRefusedRows(levelsFeed); }
    catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error(`[RangeChartPanel] /api/dealer/levels ${message}`);
      return { state: "MALFORMED" as const, message };
    }
  });
</script>

<section class="card range-card" aria-label="CHART C range chart">
  <header>
    <div>
      <p class="eyebrow">CHART C · RANGE · TIME REMOVED</p>
      <h2>ES · RANGE-BAR CVD</h2>
    </div>
    <div class="controls" role="group" aria-label="Range chart controls">
      {#each RANGE_PILLS as option}
        <button type="button" class:active={ticks === option.ticks} aria-pressed={ticks === option.ticks} onclick={() => void loadRange(option.ticks)}>{option.label}</button>
      {/each}
      <button type="button" class:active={widthMode === "minutes"} aria-pressed={widthMode === "minutes"} onclick={() => { widthMode = "minutes"; fitOnNextPaint = true; rebuildModel(); }}>width · minutes</button>
      <button type="button" class:active={widthMode === "equal"} aria-pressed={widthMode === "equal"} onclick={() => { widthMode = "equal"; fitOnNextPaint = true; rebuildModel(); }}>equal</button>
    </div>
    <StalenessChip
      label="RANGE" pollKey={feed} ageS={feed?.age_s ?? null} budgetS={feed?.budget_s ?? 180}
      error={rangeError} {lastBarMs} barSpanMs={lastBarSpanMs} compactLive />
    <AgePill payload={levelsFeed} fetchedAtMs={levelsFetchedAtMs} door={levelsRequestKey ?? "/api/dealer/levels"} />
    <span class="hyp-chip" data-hyp>HYP</span>
    {#if venueClosed}
      <VenueClosedBanner venueState={venueClosed} />
    {/if}
    {#if refusedRows.state === "REFUSED"}<span class="range-alert-pill" data-refused-rows role="status">REFUSED {refusedRows.total} {refusedRows.total === 1 ? "ROW" : "ROWS"}</span>{/if}
    {#if refusedRows.state === "MALFORMED"}<span class="range-alert-pill" data-refused-rows role="alert">{refusedRows.message}</span>{/if}
    {#if historyGapPill}
      <span class="range-alert-pill" role="status">{historyGapPill}</span>
    {/if}
    {#each levelAbsenceNotes as note}
      <span class="range-alert-pill" role="status">LEVEL ABSENT · {note}</span>
    {/each}
    <button
      type="button" class="info-button" aria-expanded={infoOpen} aria-controls="range-chart-info"
      aria-label="Chart C information" onclick={() => { infoOpen = !infoOpen; }}>ⓘ</button>
  </header>

  {#if rangeError}
    <p class="loud"><b>RANGE FEED {feed?.bars?.length ? "ERROR" : "EMPTY"}</b> · {rangeError}</p>
  {:else if !loaded}
    <p class="loading" aria-label="loading closed range bars">●</p>
  {/if}
  {#if levelsPresentation.showError}
    <p class="loud compact"><b>LEVELS ERROR</b> · {levelsError}</p>
  {/if}
  <!--
    ORDER 31-S2 · law 1 (fail LOUD): a FAILED history leg is visible ink. An EMPTY one is not —
    an absent table and the oldest end of the lake are honest successes, and marking them would
    both be provenance ink (R1106) and train the owner to ignore a chip that will one day be real.
  -->
  {#if historyError}
    <p class="loud compact" role="status"><b>RANGE HISTORY ERROR</b> · {historyError}</p>
  {/if}
  {#if infoOpen}
    <div id="range-chart-info" class="range-info-popover" role="dialog" aria-label="Chart C information">
      {#if refusedRows.state === "REFUSED"}
        <p>/api/dealer/levels</p>
        {#each refusedRows.items as item}<p>{item.field}:{item.reason} · {item.count} dropped before the levels were drawn</p>{/each}
      {:else if refusedRows.state === "UNSERVED"}
        <p>refused rows · UNSERVED (worker before 8b72948f)</p>
      {:else if refusedRows.state === "MALFORMED"}
        <p>/api/dealer/levels · {refusedRows.message}</p>
      {/if}
      <p>HEIGHT · signed contracts · COLOUR · aggressor · WIDTH · minutes</p>
      <p>GLOW · absorption cost · ⚡ · initiative · ⊠ · absorbed inside</p>
      <p>HYPOTHESIS · DISPLAY ONLY</p>
      <p>Move across the chart to read the closed bar and its real UTC minute.</p>
      {#if latestBox}
        <p>{latestBoxIsLive ? "LIVE BOX" : "LAST BOX"} {latestBox.widthTicks.toFixed(0)}t · {Math.round(latestBox.minutes)}m · net {formatContracts(latestBox.contracts)} ·
          {latestBoxIsLive ? "UNBROKEN · awaiting a closed outside bar" : latestBreak?.direction === "up" ? "BREAK ▲" : "BREAK ▼"}</p>
      {/if}
      {#if latestBreak}
        <p>box ≥35m {latestBreak.gates.duration ? "✓" : "×"} · slow bar {latestBreak.gates.speed ? "✓" : "×"} · with box Δ {latestBreak.gates.deltaAgreement ? "✓" : "×"} · bar Δ agrees {latestBreak.gates.barDeltaAgreement ? "✓" : "×"}</p>
      {/if}
      <p>{structureNotice ?? `structure: — bars · detector floor ${RANGE_STRUCTURE_FLOOR}`}</p>
      {#if historyGapPill !== null}<p>{historyGapPill}</p>{/if}
      {#each levelAbsenceNotes as note}<p>LEVEL ABSENT · {note}</p>{/each}
      {#if liveEdgeAbsentPill !== null}<p>{liveEdgeAbsentPill}</p>{/if}
      <p>normal this month · {RANGE_NORMAL_CONTRACTS} contracts / {RANGE_NORMAL_MINUTES}m</p>
      <p>real `t0/t1` retained · synthetic axis shows bar index</p>
    </div>
  {/if}

  <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
  <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
  <div class="chart-shell" style={`height:${height}px;min-height:${height}px`}>
    <div
      class="chart-host" bind:this={host} tabindex="0" role="application"
      aria-label="Interactive range chart; use left and right arrow keys to read closed bars"
      {onkeydown}></div>
    {#each structureLabels as label (label.id)}
      <span
        class="range-structure-label" data-range-structure-label={label.id}
        style={`left:${label.left}px;top:${label.top}px;width:${label.width}px;height:${label.height}px`}>{label.text}</span>
    {/each}
    {#if cursorPoint}
      <div class="range-tooltip" role="status" aria-label="Closed range bar tooltip">
        <b>{hhmm(cursorPoint.t1)}</b>
        <span>{cursorPoint.minutes}m</span>
        <span>{formatContracts(cursorPoint.contracts)} contracts</span>
        <span>{cursorPoint.cost.toFixed(1)}× cost</span>
      </div>
    {/if}
  </div>

  <!-- The face is deliberately quiet; the one information button owns definitions and readback. -->
  <div class="readback-receipt" aria-hidden="true">
    {#if closedRangeSession !== null}<span data-range-closed></span>{/if}
    {#if liveEdgeAbsentPill !== null}<span data-range-live-edge-absent></span>{/if}
    {#if historyGapPill !== null}<span data-range-history-gap></span>{/if}
  </div>
</section>

<style>
  .card { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-card); box-shadow: var(--shadow); }
  .range-card { min-width: 0; padding: 12px; }
  header { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .eyebrow { margin: 0 0 2px; color: var(--faint); font-size: 10px; font-weight: 700; letter-spacing: .12em; }
  h2 { margin: 0; color: var(--ink); font-size: 14px; font-weight: 700; letter-spacing: .03em; }
  .controls { display: flex; gap: 4px; flex-wrap: wrap; }
  button { border: 1px solid var(--line); border-radius: var(--radius-pill); background: var(--chip); color: var(--muted); padding: 4px 9px; font: inherit; font-size: 10px; font-weight: 700; cursor: pointer; }
  button.active { border-color: var(--accent); background: var(--surface-2); color: var(--ink); }
  button:focus-visible, .chart-host:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
  .info-button { margin-left: 0; padding: 2px 6px; color: var(--faint); }
  .range-alert-pill { display: inline-flex; align-items: center; max-width: 100%; border: 1px solid var(--stale); border-radius: var(--radius-pill); background: var(--stale-soft); color: var(--stale-ink); padding: 3px 7px; font-size: 10px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .hyp-chip { border: 1px solid var(--line); border-radius: var(--radius-pill); background: var(--chip); color: var(--ink); padding: 2px 6px; font: 700 10px var(--font-num); }
  .range-info-popover { margin: 8px 0; border: 1px solid var(--line); border-radius: 5px; background: var(--surface-2); color: var(--muted); padding: 8px 10px; font-size: 10px; line-height: 1.45; }
  .range-info-popover p { margin: 2px 0; }
  .chart-shell { position: relative; width: 100%; min-height: 560px; }
  .chart-host { width: 100%; height: 100%; min-height: inherit; background: var(--surface); }
  .range-structure-label { position: absolute; box-sizing: border-box; overflow: hidden; pointer-events: none; border: 1px solid var(--line); border-radius: 4px; background: var(--surface); color: var(--muted); padding: 1px 4px; font: 10px var(--font-num); line-height: 12px; white-space: nowrap; }
  .range-tooltip { position: absolute; top: 8px; right: 8px; z-index: 2; display: flex; gap: 7px; align-items: center; pointer-events: none; border: 1px solid var(--line); border-radius: 4px; background: var(--surface-2); color: var(--muted); padding: 3px 6px; font: 10px var(--font-num); white-space: nowrap; }
  .range-tooltip b { color: var(--ink); }
  .loud { margin: 8px 0; border: 1px solid var(--stale); border-radius: 5px; background: var(--stale-soft); color: var(--stale-ink); padding: 8px 10px; font-size: 11px; }
  .loud.compact { padding: 5px 8px; }
  .loading { color: var(--muted); font-size: 11px; }
  .readback-receipt { display: none; }
  @media (max-width: 700px) {
    .range-card { padding: 9px; }
    .chart-shell { height: 620px; min-height: 520px; }
  }
</style>
